import { CheckCheck, Mail, Play } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useActionsNotification, useNotifications, usePreferencesNotification } from '@/api/hooks';
import type { Notification } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { LigneNotification } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

/** Toutes mes notifications, préférences de canal, et lancement manuel des relances (DG, assistante, administrateur). */
export function Notifications() {
  const session = useSession();
  const u = session!.utilisateur;
  const navigate = useNavigate();
  const [vue, setVue] = useState<'non-lues' | 'toutes'>('non-lues');
  const liste = useNotifications(vue === 'non-lues');
  const preferences = usePreferencesNotification();
  const actions = useActionsNotification();
  const peutRelancer = u.profils.some((p) => p === 'ADMINISTRATEUR' || p === 'DIRECTEUR_GENERAL' || p === 'ASSISTANT');

  const ouvrir = (n: Notification) => {
    if (!n.lue) actions.lue.mutate(n.id);
    if (n.lien) navigate(n.lien);
  };
  const relancer = async () => {
    try {
      const r = await actions.relances.mutateAsync();
      toast.success(`Relances exécutées : ${r.rappels} rappel(s), ${r.retards} retard(s), ${r.relances} relance(s), ${r.escalades} escalade(s), ${r.alertesConsultation} alerte(s) de consultation, ${r.alertesFactures} alerte(s) facture.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Exécution impossible.');
    }
  };
  const modifier = async (champ: 'courriel' | 'recapitulatif', valeur: boolean) => {
    try {
      await actions.preferences.mutateAsync({ [champ]: valeur });
      toast.success('Préférences enregistrées.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        titre="Notifications"
        sousTitre="Documents à valider, instructions, rappels et escalades, décisions sur vos dossiers ; lien direct vers chaque document."
        actions={
          <>
            {peutRelancer && (
              <Button variant="outline" onClick={relancer} disabled={actions.relances.isPending}>
                <Play className="size-4" /> Exécuter les relances maintenant
              </Button>
            )}
            <Button variant="outline" onClick={() => actions.toutLu.mutate()} disabled={actions.toutLu.isPending}>
              <CheckCheck className="size-4" /> Tout marquer lu
            </Button>
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Tabs value={vue} onValueChange={(v) => setVue(v as typeof vue)}>
            <TabsList>
              <TabsTrigger value="non-lues">Non lues</TabsTrigger>
              <TabsTrigger value="toutes">Toutes</TabsTrigger>
            </TabsList>
          </Tabs>
          {liste.isLoading ? (
            <Chargement />
          ) : liste.error ? (
            <ErreurBloc erreur={liste.error} />
          ) : !liste.data?.length ? (
            <Vide message={vue === 'non-lues' ? 'Aucune notification non lue.' : 'Aucune notification.'} />
          ) : (
            <ul className="space-y-0.5 rounded-lg border bg-card p-1">
              {liste.data.map((n) => (
                <LigneNotification key={n.id} n={n} onOuvrir={ouvrir} />
              ))}
            </ul>
          )}
        </div>
        <Card className="self-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="size-4" /> Mes canaux
            </CardTitle>
            <CardDescription>Les notifications obligatoires (nouvelle instruction, escalade, retard) sont toujours envoyées par courriel.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <label className="flex items-center gap-2">
              <Checkbox checked={preferences.data?.courriel ?? true} onCheckedChange={(c) => modifier('courriel', c === true)} disabled={!preferences.data || actions.preferences.isPending} />
              Recevoir les autres notifications par courriel
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={preferences.data?.recapitulatif ?? false} onCheckedChange={(c) => modifier('recapitulatif', c === true)} disabled={!preferences.data || actions.preferences.isPending} />
              Récapitulatif quotidien plutôt que des courriels à l'unité (à venir)
            </label>
            {preferences.data && (
              <div className="text-xs text-muted-foreground">
                {preferences.data.modeCourriel === 'graph' ? 'Envoi des courriels actif, depuis la messagerie Microsoft 365 du FONAREV.' : preferences.data.modeCourriel === 'smtp' ? 'Envoi des courriels actif (serveur SMTP).' : "Aucun transport de courriel n'est encore configuré sur cet environnement : les courriels sont journalisés sans être envoyés."}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
